import React, {useCallback, useEffect, useRef, useState} from 'react';
import I18n from 'app/i18n';
import Get from 'app/utils/Get';
import Post from 'app/utils/Post';
import Delete from 'app/utils/Delete';
import {PopupDialog} from 'app/utils/modals/PopupDialog';
import {showPopup} from 'app/utils/modals/ModalsService';
import {preventEscPopupClose, enableEscPopupClose} from 'app/utils/modals/popups';
import {modalsStore, userStore} from 'app/store/global';
import {startGoogleAuthFlow} from 'app/utils/googleAuth';

/*
 * Import {startSteamAuthFlow} from 'app/utils/steamLink';
 */
import {ESC_KEY_CODE} from 'app/utils/Consts';

const POPUP_ID = 'external-registration';
const PENDING_META_SELECTOR = 'meta[name="external-registration-pending"][content="true"]';
const registrationUrl = (): string => `/${I18n.locale}/registration/complete.json`;

const renderRegistrationError = (error: string): React.ReactElement => <div key={error}>{error}</div>;

export const ExternalRegistrationPopup = ({popupId}: {popupId: string}): React.ReactElement => {
    const nickname = useRef<HTMLInputElement>(null);
    const inFlight = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [expired, setExpired] = useState(false);
    const [provider, setProvider] = useState<string>(null);

    const abandon = useCallback((): void => {
        document.querySelector(PENDING_META_SELECTOR)?.remove();
        new Delete({url: registrationUrl()}).execute().catch(() => null);
    }, []);

    const cancel = useCallback((): void => {
        abandon();
        modalsStore.hideModal(popupId);
    }, [abandon, popupId]);

    const finishSubmit = useCallback((): void => {
        inFlight.current = false;
        setSubmitting(false);
    }, []);

    const handleResponse = useCallback((data: {
        success?: boolean;
        redirect_url?: string;
        expired?: boolean;
        errors?: string[];
    }): void => {
        if (data.success) {
            window.location.assign(data.redirect_url);
            return;
        }
        setExpired(Boolean(data.expired));
        setErrors(data.errors || [I18n.t('external_registration.failed')]);
        finishSubmit();
    }, [finishSubmit]);

    const submit = useCallback((event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (inFlight.current) {
            return;
        }
        inFlight.current = true;
        setSubmitting(true);
        setErrors([]);
        new Post({params: {nickname: nickname.current.value}, url: registrationUrl()}).execute()
            .then(response => response.json())
            .then(handleResponse)
            .catch(() => {
                setErrors([I18n.t('external_registration.failed')]);
                finishSubmit();
            });
    }, [finishSubmit, handleResponse]);

    const handleGoogleAuthClick = useCallback((): void => {
        startGoogleAuthFlow();
    }, []);

    useEffect(() => {
        preventEscPopupClose();
        new Get({url: registrationUrl()}).execute()
            .then(response => response.json())
            .then(data => {
                setExpired(Boolean(data.expired));
                setProvider(data.provider);
            })
            .catch(() => setErrors([I18n.t('external_registration.failed')]));
        return () => {
            enableEscPopupClose();
        };
    }, []);

    useEffect(() => {
        const onKeyUp = (event: KeyboardEvent): void => {
            if (event.key === ESC_KEY_CODE) {
                cancel();
            }
        };

        document.addEventListener('keyup', onKeyUp);
        return () => document.removeEventListener('keyup', onKeyUp);
    }, [cancel]);

    return <PopupDialog id={popupId} setFocus onClose={abandon}>
        <div className="popup__content popup__content--small" role="dialog" aria-modal="true"
            aria-labelledby="external-registration-title">
            <h2 id="external-registration-title">
                {I18n.t(`external_registration.${expired ? 'expired_title' : 'title'}`)}
            </h2>
            {expired
                ? <>
                    <p>{I18n.t('external_registration.expired')}</p>
                    <div className="popup-form__submit-row popup-form__submit-row--pair">
                        {provider !== 'steam' && <button type="button" className="btn btn--large"
                            onClick={handleGoogleAuthClick}>Google</button>}
                        {/*
                        {provider !== 'google' && <button type="button" className="btn btn--large"
                            onClick={() => startSteamAuthFlow()}>Steam</button>}
                        */}
                    </div>
                </>
                : <form className={`popup-form${errors.length > 0 ? ' popup-form--error' : ''}`} onSubmit={submit}>
                    <p>{I18n.t('external_registration.description')}</p>
                    {errors.length > 0 && <div className="popup-form__error-hint" role="alert">
                        {errors.map(renderRegistrationError)}
                    </div>}
                    <div className="popup-form__row">
                        <label htmlFor="external-registration-nickname">{I18n.t('external_registration.nickname')}</label>
                        <div className={`field${errors.length > 0 ? ' field-error' : ''}`}>
                            <input ref={nickname} id="external-registration-nickname" name="nickname"
                                className="field__input" type="text" required minLength={4} maxLength={32}
                                autoComplete="nickname" aria-describedby="external-registration-hint"/>
                        </div>
                        <p id="external-registration-hint" className="popup-form__hint">
                            {I18n.t('external_registration.hint')}
                        </p>
                    </div>
                    <div className="popup-form__submit-row popup-form__submit-row--pair">
                        <button className="btn btn--large" type="submit" disabled={submitting}>
                            {I18n.t('external_registration.submit')}
                        </button>
                        <button className="btn btn--large btn--invert" type="button" onClick={cancel}>
                            {I18n.t('external_registration.cancel')}
                        </button>
                    </div>
                </form>}
        </div>
    </PopupDialog>;
};

const renderExternalRegistrationPopup = function(props: {popupId: string}): React.ReactElement {
    return <ExternalRegistrationPopup popupId={props.popupId}/>;
};

export const showExternalRegistration = (): boolean => {
    if (userStore.user.user_id || !document.querySelector(PENDING_META_SELECTOR)) {
        return false;
    }
    preventEscPopupClose();
    if (!modalsStore.isModalOpen(POPUP_ID)) {
        showPopup(POPUP_ID, renderExternalRegistrationPopup);
    }
    return true;
};
